import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useError } from './ErrorContext';
import { useAuth } from './AuthContext';
import { useLoading } from './LoadingContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const ProductContext = createContext();

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};

export const ProductProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [ setApi] = useState(null);
  const { handleApiError, formErrors, clearErrors } = useError();
  const [products, setProducts] = useState([]);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/products');

  const getProducts = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
   
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
        
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }
      let response = await apiInstance.call('products?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        response = await apiInstance.call('products');  
      }
      
      if (response.status === 200) {
        setPaginate(response.data.paginate)
        setProducts(response.data.products || []);
       
        
        if(filters){
          return response.data.products;
        }
      } else {
        
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch products information'
        });
      }
    } catch (error) {
     
    } finally {
      
    }
  }, [handleApiError, paginate, getToken]);

  const getProductByIdApi = useCallback(async (id, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`products/${id}`, 'GET', null, true, false);
   
    if (response.status === 200) {
      
      let products = {'data':[response.data.product]}
      setProducts(products || []);
      
    } 
  }, [getToken]);

  useEffect(() => {
    
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/products/edit/'+id))){
        if (getToken()) {
         
          await getProductByIdApi(id);
        }
      }
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getProductByIdApi, searchParams,setApi]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
      try {
     
        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
         
          await getProducts(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
   
    if(initialized){
      initializeApi();
    }
  }, [getToken, getProducts, searchParams, initialized,hideLoading,setApi]);

  const getProductAdditionalData = useCallback(async () => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call('get-products-data', 'GET', null, true);
    if(response.status === 200){
       return response.data;
    } else{
      return handleApiError(response.error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to get product data'
      });
    } 
  }, [getToken, handleApiError]);

  const addProduct = async (data) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {

      let formData = new FormData();

      // Append images (files)
      if (data.images) {
        data.images.forEach((item, index) => {
          formData.append(`images[${index}][id]`, item.id);
          formData.append(`images[${index}][file]`, item.file);
          formData.append(`images[${index}][name]`, item.name);
          formData.append(`images[${index}][size]`, item.size);
          formData.append(`images[${index}][type]`, item.type);
          formData.append(`images[${index}][created_at]`, item.created_at);
         
        });
      }
      
      // Remove images from the original object so we can serialize the rest
      const dataCopy = { ...data };
      delete dataCopy.images;
      
      // Append the rest of the data as JSON
      formData.append('data', JSON.stringify(dataCopy));

      const response = await apiInstance.call('products', 'POST', formData, true,true);
      
      if (response.status === 201 || response.status === 200) {
        // Refresh the products list
        await getProducts();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add Product'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to create product'
      });
      return { status: 500, error: error.message };
    }
  };

  const getProduct = (id) => {
    return products?.data?.find(product => Number(product.id) === Number(id));
  };

  const getProductById = useCallback(async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`products/${id}`, 'GET', null, true);
      
      if (response.status === 200) {
        return { status: response.status, data: response.data.product };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to fetch product details'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to fetch product details'
      });
      return { status: 500, error: error.message };
    }
  }, [getToken, handleApiError]);

  const updateProduct = async (id, updatedData) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      let formData = new FormData();

      // Append images (files)
      if (updatedData.images) {
        updatedData.images.forEach((item, index) => {
          formData.append(`images[${index}][id]`, item.id);
          formData.append(`images[${index}][file]`, item.file);
          formData.append(`images[${index}][name]`, item.name);
          formData.append(`images[${index}][size]`, item.size);
          formData.append(`images[${index}][type]`, item.type);
          formData.append(`images[${index}][created_at]`, item.created_at);
         
        });
      }
      
      // Remove images from the original object so we can serialize the rest
      const dataCopy = { ...updatedData };
      delete dataCopy.images;
      
      // Append the rest of the data as JSON
      formData.append('data', JSON.stringify(dataCopy));
      
      // Now send the request with formData only once
      const response = await apiInstance.call(`products/${id}`, 'POST', formData, true, true);
      
      if (response.status === 200) {
        // Refresh the products list
        await getProducts();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
             errors: response.error.validationErrors,
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update Product'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to update product'
      });
      return { status: 500, error: error.message };
    }
  };

  const deleteProduct = async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`products/${id}`, 'DELETE', null, true);
      
      if (response.status === 200) {
        // Refresh the products list
        await getProducts();
        navigate(getRoutePath('/products'));
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to delete product'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete product'
      });
      return { status: 500, error: error.message };
    }
  };

  return (
    <ProductContext.Provider
      value={{
        products,
        addProduct,
        getProduct,
        getProductById,
        updateProduct,
        deleteProduct,
        getProducts,
        getProductAdditionalData,
        formErrors,
        clearErrors
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

export default ProductContext; 